// Off-road bike visual: a simple enduro silhouette that follows the player's
// bike heading (physics live in Player._steerBike). Scene-level like the
// jet-suit — the rig itself only carries the camera.
//
// Layout: group origin = the player's ground point, so the eye rides ~1.6 m
// over the wheels. The bar is therefore mounted HIGH (see the pivot block) —
// at stock height it falls out of frame when looking where you ride. Local -Z is forward
// (group.rotation.y = bikeYaw maps local -Z onto the travel heading — no +PI:
// that rendered the whole bike backwards). Wheels roll about the X axle
// (torus pre-rotated; spokes make the spin readable) and the entire front
// assembly (fork + bar + wheel + fender) hangs off a steering pivot that
// mirrors player.bikeSteer.
import * as THREE from 'three';

export class BikeView {
  constructor(scene) {
    const g = new THREE.Group();
    g.name = 'bike';
    g.visible = false;
    g.rotation.order = 'YXZ';
    const dark = new THREE.MeshLambertMaterial({ color: 0x24262b });
    const red = new THREE.MeshLambertMaterial({ color: 0xc23c2e });
    const alu = new THREE.MeshLambertMaterial({ color: 0x9aa0a8 });
    const tire = new THREE.MeshLambertMaterial({ color: 0x181a1c });

    // wheel: torus ring in the ZY plane (axle = X) + hub + spokes
    const wheelGeo = new THREE.TorusGeometry(0.33, 0.09, 8, 16);
    wheelGeo.rotateY(Math.PI / 2);
    const mkWheel = () => {
      const w = new THREE.Group();
      w.add(new THREE.Mesh(wheelGeo, tire));
      const hub = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.1, 8), alu);
      hub.rotation.z = Math.PI / 2;
      w.add(hub);
      for (let i = 0; i < 3; i++) {
        const sp = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.6, 0.035), alu);
        sp.rotation.x = i * Math.PI / 3;
        w.add(sp);
      }
      return w;
    };

    this.wheelR = mkWheel();
    this.wheelR.position.set(0, 0.33, 0.32);
    g.add(this.wheelR);

    const frame = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.3, 1.1), red);
    frame.position.set(0, 0.62, -0.34);
    frame.rotation.x = 0.12;
    const tank = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.16, 0.42), red);
    tank.position.set(0, 0.84, -0.52);
    const seat = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.07, 0.5), dark);
    seat.position.set(0, 0.86, 0);
    g.add(frame, tank, seat);
    // head tube: the raised bar would otherwise float free of the frame
    const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.44, 6), dark);
    neck.position.set(0, 1.02, -0.73);
    neck.rotation.x = -0.82;              // +Y end leans forward, up to the pivot
    g.add(neck);

    // steering pivot at the head tube — everything in front turns with it.
    // BAR HEIGHT IS A VR AFFORDANCE, not moto realism: the eye rides at
    // standHeight (~1.6 m over the wheels), so a stock ~1.1 m bar sits ~35°
    // below the horizon and is simply not in frame when looking where you
    // ride. 1.37 m and 0.85 m ahead puts it ~15° down — in view, hands where
    // the player expects them.
    const pivot = new THREE.Group();
    pivot.position.set(0, 1.16, -0.88);
    for (const sx of [-0.075, 0.075]) {
      const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.86, 6), alu);
      leg.position.set(sx, -0.415, -0.11);
      // bottom end forward (+angle): the axle leads the steering axis. The
      // old -0.42 raked the tubes BACKWARD while the wheel sat forward, so
      // the fork visibly missed its own axle.
      leg.rotation.x = 0.26;
      pivot.add(leg);
    }
    const clamp = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.05, 0.11), dark);
    clamp.position.set(0, 0.02, 0.0);
    pivot.add(clamp);
    const riser = new THREE.Mesh(new THREE.CylinderGeometry(0.032, 0.032, 0.2, 6), dark);
    riser.position.set(0, 0.11, 0.02);
    pivot.add(riser);
    const bar = new THREE.Mesh(new THREE.CylinderGeometry(0.024, 0.024, 0.68, 6), alu);
    bar.rotation.z = Math.PI / 2;
    bar.position.set(0, 0.21, 0.03);
    pivot.add(bar);
    const pad = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.07, 0.07), red);
    pad.position.set(0, 0.21, 0.03);
    pivot.add(pad);
    for (const sx of [-0.32, 0.32]) {
      const grip = new THREE.Mesh(new THREE.CylinderGeometry(0.032, 0.032, 0.14, 6), dark);
      grip.rotation.z = Math.PI / 2;
      grip.position.set(sx, 0.21, 0.03);
      pivot.add(grip);
      const lever = new THREE.Mesh(new THREE.BoxGeometry(0.13, 0.015, 0.03), alu);
      lever.position.set(sx - Math.sign(sx) * 0.09, 0.20, -0.06);
      pivot.add(lever);
    }
    const fender = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.03, 0.5), red);
    fender.position.set(0, -0.42, -0.20);
    fender.rotation.x = -0.3;
    pivot.add(fender);
    this.wheelF = mkWheel();
    this.wheelF.position.set(0, -0.83, -0.22);
    pivot.add(this.wheelF);
    g.add(pivot);
    this.pivot = pivot;

    // turn indicators: amber lamps on both sides (bar ends + tail), blinked
    // in update() from player.bikeInd. Rider's right = +X (facing local -Z).
    const indMat = new THREE.MeshBasicMaterial({ color: 0xffa424 });
    this.indL = []; this.indR = [];
    for (const sx of [-1, 1]) {
      const arr = sx < 0 ? this.indL : this.indR;
      const front = new THREE.Mesh(new THREE.SphereGeometry(0.045, 8, 6), indMat);
      front.position.set(sx * 0.40, 0.21, 0.03);   // just past the grip
      pivot.add(front);
      const rear = new THREE.Mesh(new THREE.SphereGeometry(0.045, 8, 6), indMat);
      rear.position.set(sx * 0.18, 0.80, 0.42);    // beside the tail
      g.add(rear);
      arr.push(front, rear);
      front.visible = rear.visible = false;
    }

    scene.add(g);
    this.group = g;
    this._spin = 0;
  }

  update(player, dt = 0.016) {
    const on = player.bike;
    this.group.visible = on;
    if (!on) return;
    this.group.position.set(player.pos.x, player.pos.y, player.pos.z);
    const steer = player.bikeSteer || 0;
    // lean into the turn, more with speed; steer>0 = right turn = right lean
    const lean = steer * Math.min(1, player.bikeSpeed / 8) * 0.30;
    this.group.rotation.set(0, player.bikeYaw, -lean);
    // handlebar mirrors the steering input; barely turns at speed, like a
    // real bike (countersteer fidelity is not worth the visual confusion)
    this.pivot.rotation.y = -steer * (0.5 - 0.35 * Math.min(1, player.bikeSpeed / 12));
    this._spin += player.bikeSpeed / 0.33 * dt;
    this.wheelF.rotation.x = -this._spin;
    this.wheelR.rotation.x = -this._spin;
    // blink the signalled side (same 0.9 s cadence as remote avatars)
    const blinkOn = (performance.now() % 900) < 500;
    for (const l of this.indL) l.visible = player.bikeInd === -1 && blinkOn;
    for (const l of this.indR) l.visible = player.bikeInd === 1 && blinkOn;
  }
}
